import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const useSessionTimeout = (timeoutMinutes = 5) => {
  const { logout } = useAuth();
  const timeoutRef = useRef(null);
  const timeoutMinutesRef = useRef(timeoutMinutes);
  const debounceRef = useRef(null);

  // Update the ref when timeoutMinutes changes
  timeoutMinutesRef.current = timeoutMinutes;

  const resetTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      // Direct redirect without showing timeout page
      logout().then(() => {
        window.location.href = 'https://epoch-d2996.web.app/';
      }).catch(error => {
        console.error('Error during logout:', error);
        // Still redirect even if logout fails
        window.location.href = 'https://epoch-d2996.web.app/';
      });
    }, timeoutMinutesRef.current * 60 * 1000);
  }, [logout]);

  const clearSessionTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Immediate activity handler - no debouncing to prevent timeouts during active use
  const handleActivity = useCallback(() => {
    // Clear existing debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Immediate reset for active users, with minimal debounce only for rapid events
    debounceRef.current = setTimeout(() => {
      resetTimeout();
    }, 100); // Reduced from 1000ms to 100ms
  }, [resetTimeout]);

  useEffect(() => {
    // Start the timer when component mounts
    resetTimeout();

    // Event listeners for user activity (removed 'blur' and 'focus' to prevent tab switching issues)
    const activityEvents = [
      'mousedown', 
      'mousemove', 
      'keypress', 
      'keydown',
      'scroll', 
      'touchstart', 
      'click',
      'wheel',
      'resize'
    ];
    
    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Cleanup on unmount
    return () => {
      clearSessionTimeout();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [resetTimeout, clearSessionTimeout, handleActivity]);

  return { 
    resetTimeout, 
    clearTimeout: clearSessionTimeout
  };
};

export default useSessionTimeout;
